import { useCallback, useEffect, useRef, useState, type FC } from "react";
import styled from "styled-components";

import { useRootDispatch, useRootSelector } from "@ds/hooks";

import { HeaderTitle } from "@ds/components/header-title";
import { Loading } from "@ds/components/loaders/loading";
import { logger } from "@ds/utils/logger";

import {
  selectCurrentUserProject,
  selectProjectReportData,
  selectProjectsIsLoading,
} from "../settings/projects/redux/selectors/common-selectors";
import { ProjectsRemoteOperation, projectsActions } from "../settings/projects/redux/slice";
import { loadTableauEmbeddingApi } from "./tableau-loader";

const VizContainer = styled.div`
  height: 100%;

  tableau-viz {
    display: block;
    height: 100%;
    width: 100%;
  }
`;

export const ReportsMainPage: FC = () => {
  const project = useRootSelector(selectCurrentUserProject);
  const report = useRootSelector(selectProjectReportData);
  const isLoading = useRootSelector(state => selectProjectsIsLoading(state, ProjectsRemoteOperation.REPORT));
  const [isApiReady, setIsApiReady] = useState(false);
  const vizRef = useRef<HTMLElement | null>(null);

  const dispatch = useRootDispatch();

  // Fetch (or refresh) a short-lived embed token when missing or expired.
  useEffect(() => {
    const isTokenExpired = !report.expiredAt || report.expiredAt < Date.now();
    if (project && isTokenExpired) {
      dispatch(projectsActions.fetchProjectReport(project.id));
    }
  }, [dispatch, project, report.expiredAt]);

  // Load the Tableau Embedding API v3 from the embed's pod once we have a URL.
  useEffect(() => {
    if (report.embedUrl) {
      loadTableauEmbeddingApi(report.embedUrl)
        .then(() => setIsApiReady(true))
        .catch(() => setIsApiReady(false));
    }
  }, [report.embedUrl]);

  // Surface the precise Tableau error (e.g. code 10084) instead of failing silently.
  const handleVizLoadError = useCallback((event: Event) => {
    const detail = (event as CustomEvent).detail;
    logger.error(`Tableau viz failed to load: ${JSON.stringify(detail)}`);
  }, []);

  useEffect(() => {
    const viz = vizRef.current;
    if (!viz) {
      return;
    }
    viz.addEventListener("vizloaderror", handleVizLoadError);
    return () => viz.removeEventListener("vizloaderror", handleVizLoadError);
  }, [handleVizLoadError, isApiReady, report.embedUrl, report.token]);

  return (
    <>
      <HeaderTitle title="Reports" />
      <Loading isLoading={isLoading}>
        {isApiReady && report.embedUrl && report.token && (
          <VizContainer>
            <tableau-viz
              style={{ width: "1610px" }}
              ref={vizRef}
              src={report.embedUrl}
              token={report.token}
              toolbar="hidden"
            />
          </VizContainer>
        )}
      </Loading>
    </>
  );
};
